// src/pages/Appli/AppliAdmin/AlbumsPhotos/EditAlbumModal.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaTimes, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../../../components/Button/Button";
import { updateGallery } from "../../../../services/gallerieServices";
import { LanguageContext } from "../../../../contexts/LanguageContext";

const normLang = (l) => {
  const v = String(l || "fr").toLowerCase();
  if (v.startsWith("en")) return "en";
  return "fr";
};

const EditAlbumModal = ({ album, onClose, onSave }) => {
  const { language } = useContext(LanguageContext);
  const [localLanguage, setLocalLanguage] = useState(normLang(language));

  const lang = useMemo(() => normLang(localLanguage), [localLanguage]);

  const [formData, setFormData] = useState({
    id: album?.id || "",
    titre_fr: album?.titre_fr || "",
    titre_en: album?.titre_en || "",
    description_fr: album?.description_fr || "",
    description_en: album?.description_en || "",
    // kanji existe en DB -> on le conserve mais sans UI
    kanji: album?.kanji || "",
    is_active: album?.is_active ?? true,
  });

  useEffect(() => {
    if (!album?.id) return;
    setFormData({
      id: album.id,
      titre_fr: album.titre_fr || "",
      titre_en: album.titre_en || "",
      description_fr: album.description_fr || "",
      description_en: album.description_en || "",
      kanji: album.kanji || "",
      is_active: album.is_active ?? true,
    });
  }, [album]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, [`description_${lang}`]: value }));
  };

  const handleSave = async () => {
    try {
      await updateGallery(formData);
      toast.success("Album mis à jour ✅");
      onSave?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'album :", error);
      toast.error("Erreur mise à jour album.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        <div className="bg-primary text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-extrabold">Modifier l’album</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-3 py-2 bg-white/15 hover:bg-white/20 transition"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-5">
          {/* Lang */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
              Langue d’édition
            </label>
            <select
              value={lang}
              onChange={(e) => setLocalLanguage(e.target.value)}
              className="w-full rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white outline-none"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Titre ({lang.toUpperCase()})
            </label>
            <input
              type="text"
              name={`titre_${lang}`}
              value={formData[`titre_${lang}`] || ""}
              onChange={handleChange}
              className="w-full rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
              placeholder={`Titre (${lang.toUpperCase()})`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Description ({lang.toUpperCase()})
            </label>
            <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20">
              <ReactQuill
                value={formData[`description_${lang}`] || ""}
                onChange={handleContentChange}
              />
            </div>
          </div>

          {/* Actif */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="is_active"
              checked={!!formData.is_active}
              onChange={handleChange}
              className="h-5 w-5 accent-primary"
            />
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Album actif
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex justify-end gap-3">
          <Button text="Annuler" onClick={onClose} icon={FaTimes} className="gap-2" />
          <Button text="Enregistrer" onClick={handleSave} icon={FaSave} className="gap-2" />
        </div>
      </div>
    </div>
  );
};

export default EditAlbumModal;
